"use client";
import * as React from "react";
import {
  Copy,
  Check,
  AlertCircle,
  CheckCircle,
  XCircle,
  X,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  ScrollArea,
} from "visuais";

/**
 * Componente para toast de erro simplificado
 */
function ProtheusErrorToast({ mensagem }: { mensagem: string }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const mensagemLimpa = mensagem
    .replace(/\[ERRO\]/g, "")
    .replace(/AJUDA:EXISTNF/g, "Nota Fiscal já cadastrada:")
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\r\n|\n|\r/g, "\n")
    .replace(/\\t/g, "  ");

  const linhas = mensagemLimpa
    .split(/\n+/)
    .map((linha) => linha.trim())
    .filter(Boolean);

  // Tenta identificar o tipo de erro para título/subtítulo
  const isNotaExistente = mensagem.includes("AJUDA:EXISTNF");
  const titulo = isNotaExistente
    ? "Nota Fiscal já cadastrada"
    : "Erro ao salvar pré-nota";
  const subtitulo = isNotaExistente
    ? "Já existe uma pré-nota com os mesmos dados."
    : "Ocorreu um erro ao tentar salvar a pré-nota.";

  const handleCopy = async () => {
    try {
      // Verifica se estamos no cliente (browser)
      if (typeof window === "undefined") {
        console.warn("Clipboard não disponível no servidor");
        return;
      }

      // Para HTTP, sempre usa execCommand (mais confiável)
      const textArea = document.createElement("textarea");
      textArea.value = mensagemLimpa;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      document.body.appendChild(textArea);

      // Força o foco e seleção
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error("Falha ao copiar com execCommand");
        // Tenta novamente com uma abordagem diferente
        try {
          const tempInput = document.createElement("input");
          tempInput.value = mensagemLimpa;
          document.body.appendChild(tempInput);
          tempInput.select();
          document.execCommand("copy");
          document.body.removeChild(tempInput);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (retryErr) {
          console.error("Falha na segunda tentativa:", retryErr);
        }
      }
    } catch (err) {
      console.error("Erro ao copiar:", err);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between font-sans max-w-[400px] border bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800 rounded-lg p-3 shadow-lg">
        <div className="flex flex-col gap-1">
          <span className="text-red-700 dark:text-red-300 font-bold text-sm">
            {titulo}
          </span>
          <span className="text-xs text-red-800 dark:text-red-400 leading-tight">
            {subtitulo}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-100 border-red-300 dark:border-red-800 dark:hover:bg-red-950/40"
          onClick={() => setIsExpanded(true)}
        >
          Visualizar Detalhes
        </Button>
      </div>

      {/* Drawer para conteúdo expandido */}
      <Drawer open={isExpanded} onOpenChange={setIsExpanded} direction="right">
        <DrawerContent className="w-[43%] min-w-[700px] max-h-[100vh] z-[9999]">
          <DrawerHeader>
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-red-700 dark:text-red-300 text-xl">
                {titulo}
              </DrawerTitle>
              
            </div>
          </DrawerHeader>
          <div className="px-6 pb-6">
            <div className="h-[90vh] p-4 w-full rounded border bg-red-100 border-red-200 dark:bg-red-950/40 dark:border-red-800 overflow-auto">
              <pre className="whitespace-pre-wrap text-red-900 dark:text-red-300 leading-relaxed break-all select-text text-[7pt]">
                {mensagemLimpa}
              </pre>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/**
 * Formata mensagens de erro do Protheus para exibição em toast customizado.
 * Apresenta título, subtítulo amigável e detalhes técnicos em tabela monoespaçada estilizada com Tailwind.
 *
 * @param mensagem Mensagem de erro bruta retornada pelo Protheus (pode conter tags, quebras de linha e códigos internos)
 * @returns JSX formatado para uso em toast customizado (react-hot-toast)
 */
export function formatProtheusError(mensagem: string): React.JSX.Element {
  return <ProtheusErrorToast mensagem={mensagem} />;
}

/**
 * Componente para toast de sucesso com múltiplos status
 */
function ProtheusSuccessToast({ resposta }: { resposta: any }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  // Extrai campos relevantes
  const prenotaSucesso =
    resposta?.Prenota === true ||
    resposta?.Sucesso === true ||
    resposta?.REC != null;
  const msgPrenota = resposta.MsgPrenota;
  const documento = resposta.Documento;
  const rec = resposta.REC;

  // Determina o status da classificação
  const classificacaoSucesso =
    resposta.Classificacao === true || resposta.ClassificacaoSucesso === true;

  // Determina o status geral e ícones
  const statusGeral = prenotaSucesso ? "success" : "error";
  const titulo = prenotaSucesso
    ? "Pré-nota salva com sucesso!"
    : "Erro ao salvar pré-nota";

  // Monta linhas de detalhes
  const detalhes: string[] = [];
  if (documento) detalhes.push(`Documento: ${documento}`);
  if (rec) detalhes.push(`REC: ${rec}`);
  if (msgPrenota) detalhes.push(`Pré-nota: ${msgPrenota}`);

  const handleCopy = async () => {
    try {
      const conteudoParaCopiar = detalhes.join("\n");

      // Verifica se estamos no cliente (browser)
      if (typeof window === "undefined") {
        console.warn("Clipboard não disponível no servidor");
        return;
      }

      // Para HTTP, sempre usa execCommand (mais confiável)
      const textArea = document.createElement("textarea");
      textArea.value = conteudoParaCopiar;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      document.body.appendChild(textArea);

      // Força o foco e seleção
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error("Falha ao copiar com execCommand");
        // Tenta novamente com uma abordagem diferente
        try {
          const tempInput = document.createElement("input");
          tempInput.value = conteudoParaCopiar;
          document.body.appendChild(tempInput);
          tempInput.select();
          document.execCommand("copy");
          document.body.removeChild(tempInput);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (retryErr) {
          console.error("Falha na segunda tentativa:", retryErr);
        }
      }
    } catch (err) {
      console.error("Erro ao copiar:", err);
    }
  };

  return (
    <>
      <div
        className={`flex items-center justify-between font-sans max-w-[400px] border rounded-lg p-3 shadow-lg ${
          statusGeral === "success"
            ? "bg-green-50 dark:bg-green-950/40 border-green-300 dark:border-green-800"
            : "bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800"
        }`}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {prenotaSucesso ? (
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            ) : (
              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            )}
            <span
              className={`font-bold text-sm ${
                statusGeral === "success"
                  ? "text-green-700 dark:text-green-300"
                  : "text-red-700 dark:text-red-300"
              }`}
            >
              {titulo}
            </span>
          </div>
          <div className="flex flex-col gap-1 text-xs">
            {/* Status da Pré-nota */}
            <div className="flex items-center gap-1">
              {prenotaSucesso ? (
                <CheckCircle className="w-3 h-3 text-green-600" />
              ) : (
                <XCircle className="w-3 h-3 text-red-600" />
              )}
              <span
                className={
                  prenotaSucesso
                    ? "text-green-800 dark:text-green-400"
                    : "text-red-800 dark:text-red-400"
                }
              >
                Pré-nota: {prenotaSucesso ? "Salva" : "Erro"}
              </span>
            </div>

            {/* Status da Classificação */}
            <div className="flex items-center gap-1">
              {classificacaoSucesso ? (
                <CheckCircle className="w-3 h-3 text-green-600" />
              ) : (
                <AlertCircle className="w-3 h-3 text-yellow-600" />
              )}
              <span
                className={
                  classificacaoSucesso
                    ? "text-green-800 dark:text-green-400"
                    : "text-yellow-800 dark:text-yellow-400"
                }
              >
                Classificação: {classificacaoSucesso ? "Aplicada" : "Erro"}
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className={`${
            statusGeral === "success"
              ? "text-green-600 hover:text-green-700 hover:bg-green-100 border-green-300 dark:border-green-800 dark:hover:bg-green-950/40"
              : "text-red-600 hover:text-red-700 hover:bg-red-100 border-red-300 dark:border-red-800 dark:hover:bg-red-950/40"
          }`}
          onClick={() => setIsExpanded(true)}
        >
          Visualizar Detalhes
        </Button>
      </div>

      {/* Drawer para conteúdo expandido */}
      <Drawer open={isExpanded} onOpenChange={setIsExpanded} direction="right">
        <DrawerContent className="w-[43%] max-w-[550px] min-w-[440px] max-h-[100vh] z-[9999]">
          <DrawerHeader>
            <div className="flex items-center justify-between">
              <DrawerTitle
                className={`text-xl ${
                  statusGeral === "success"
                    ? "text-green-700 dark:text-green-300"
                    : "text-red-700 dark:text-red-300"
                }`}
              >
                {titulo}
              </DrawerTitle>
              {detalhes.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className={`${
                    statusGeral === "success"
                      ? "text-green-600 hover:text-green-700 hover:bg-green-100 border-green-300 dark:border-green-800 dark:hover:bg-green-950/40 dark:text-green-300"
                      : "text-red-600 hover:text-red-700 hover:bg-red-100 border-red-300 dark:border-red-800 dark:hover:bg-red-950/40 dark:text-red-300"
                  }`}
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? "Copiado!" : "Copiar"}
                </Button>
              )}
            </div>
          </DrawerHeader>
          <div className="px-6 pb-6">
            {detalhes.length > 0 && (
              <div
                className={`h-[90vh] p-4 w-full rounded border overflow-auto ${
                  statusGeral === "success"
                    ? "bg-green-100 border-green-200 dark:bg-green-950/40 dark:border-green-800"
                    : "bg-red-100 border-red-200 dark:bg-red-950/40 dark:border-red-800"
                }`}
              >
                <pre
                  className={`whitespace-pre-wrap leading-relaxed break-all select-text ${
                    statusGeral === "success"
                      ? "text-green-900 dark:text-green-300"
                      : "text-red-900 dark:text-red-300"
                  }`}
                >
                  {detalhes.join("\n")}
                </pre>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/**
 * Formata resposta de sucesso do Protheus para exibição em toast customizado.
 * Apresenta título, mensagem principal e detalhes técnicos em tabela monoespaçada estilizada com Tailwind.
 *
 * @param resposta Objeto de resposta de sucesso retornado pelo Protheus
 * @returns JSX formatado para uso em toast customizado (react-hot-toast)
 */
export function formatProtheusSuccess(resposta: any): React.JSX.Element {
  return <ProtheusSuccessToast resposta={resposta} />;
}

/**
 * Toast simples para upload de arquivos
 */
export function formatUploadSuccess(): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 font-sans max-w-[400px] border bg-blue-50 dark:bg-blue-950 border-blue-300 dark:border-blue-800 rounded-lg p-3 shadow-lg">
      <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
      <div className="flex flex-col gap-1">
        <span className="text-blue-700 dark:text-blue-300 font-bold text-sm">
          Arquivos recebidos!
        </span>
        <span className="text-xs text-blue-800 dark:text-blue-400 leading-tight">
          Os anexos foram enviados com sucesso.
        </span>
      </div>
    </div>
  );
}

/**
 * Toast de loading com botão de cancelar
 */
export function formatLoadingToast(onCancel: () => void): React.JSX.Element {
  return (
    <div className="flex items-center justify-between font-sans max-w-[400px] border bg-blue-50 dark:bg-blue-950 border-blue-300 dark:border-blue-800 rounded-lg p-3 shadow-lg">
      <div className="flex items-center gap-3">
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
        <div className="flex flex-col gap-1">
          <span className="text-blue-700 dark:text-blue-300 font-bold text-sm">
            Salvando pré-nota...
          </span>
          <span className="text-xs text-blue-800 dark:text-blue-400 leading-tight">
            Aguarde enquanto processamos sua solicitação.
          </span>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onCancel}
        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 border-blue-300 dark:border-blue-800 dark:hover:bg-blue-950/40 dark:text-blue-300"
      >
        <X className="h-4 w-4" />
        Cancelar
      </Button>
    </div>
  );
}

type ValidationState = "pendente" | "sucesso" | "erro";

interface ValidationToastProps {
  prenota: ValidationState;
  anexo: ValidationState;
  rec: string;
  detalhe?: string;
}

function renderStatusBadge(label: string, state: ValidationState) {
  if (state === "sucesso") {
    return (
      <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">{label}: concluido</span>
      </div>
    );
  }

  if (state === "erro") {
    return (
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
        <XCircle className="w-4 h-4" />
        <span className="text-sm font-medium">{label}: erro</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
      <AlertCircle className="w-4 h-4" />
      <span className="text-sm font-medium">{label}: em validacao</span>
    </div>
  );
}

function ValidationMonitorToast({
  prenota,
  anexo,
  rec,
  detalhe,
}: ValidationToastProps) {
  const allSuccess = prenota === "sucesso" && anexo === "sucesso";
  const hasError = prenota === "erro" || anexo === "erro";
  const isBlocking = !allSuccess && !hasError;

  const boxClass = allSuccess
    ? "bg-green-50 dark:bg-green-950/40 border-green-300 dark:border-green-800"
    : hasError
      ? "bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800"
      : "bg-yellow-50 dark:bg-yellow-950/40 border-yellow-300 dark:border-yellow-800";

  const titleClass = allSuccess
    ? "text-green-700 dark:text-green-300"
    : hasError
      ? "text-red-700 dark:text-red-300"
      : "text-yellow-700 dark:text-yellow-300";

  const titulo = allSuccess
    ? "Validacao concluida"
    : hasError
      ? "Validacao com erro"
      : "Validacao em andamento";

  if (isBlocking) {
    return (
      <Dialog open>
        <DialogContent
          className="w-[min(92vw,560px)] z-[2147483647]"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className={`font-bold text-sm ${titleClass}`}>
              {titulo} - REC {rec}
            </DialogTitle>
          </DialogHeader>
          <div className={`font-sans border rounded-lg p-3 shadow-lg ${boxClass}`}>
            <div className="flex flex-col gap-3">
              {renderStatusBadge("Pre-nota", prenota)}
              {renderStatusBadge("Anexo", anexo)}
              {detalhe ? (
                <span className="text-xs text-slate-700 dark:text-slate-300 leading-tight">
                  {detalhe}
                </span>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className={`font-sans max-w-[460px] border rounded-lg p-3 shadow-lg ${boxClass}`}>
      <div className="flex flex-col gap-3">
        <span className={`font-bold text-sm ${titleClass}`}>
          {titulo} - REC {rec}
        </span>
        {renderStatusBadge("Pre-nota", prenota)}
        {renderStatusBadge("Anexo", anexo)}
        {detalhe ? (
          <span className="text-xs text-slate-700 dark:text-slate-300 leading-tight">
            {detalhe}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function formatValidationMonitorToast(
  payload: ValidationToastProps
): React.JSX.Element {
  return <ValidationMonitorToast {...payload} />;
}
